import React, { useState } from 'react';
import { Mail, Phone, Globe, MapPin, Briefcase, Building, Pencil, Images, Play, Trash2, ChevronDown, ChevronUp, ExternalLink, Sparkles } from 'lucide-react';
import { BioRichTextDisplay } from './BioMediaDisplay';
import { BrandIcons, getSocialLinks, getExtraLinks, extractYouTubeVideoId } from './BrandIcons';
import { GalleryGrid } from './GalleryLightbox';

const BioTemplateModern = ({ card, bioUrl, constructSocialUrl, trackLinkClick, onQRClick, hideQR = false, isOwner = false, onUpdateProfileImage, onUpdateLogo, isPremium = false }) => {
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const hasGallery = card.galleryImages && card.galleryImages.length > 0;
  const hasVideos = card.youtubeVideos && card.youtubeVideos.length > 0;
  const socialLinks = getSocialLinks(card);
  const extraLinks = getExtraLinks(card);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-100 via-pink-50 to-teal-50">
      <div className="max-w-md mx-auto">
        <div className="bg-white/80 backdrop-blur-lg shadow-2xl overflow-visible">
          {/* Header with Gradient */}
          <div className="relative h-32 sm:h-36 bg-gradient-to-br from-amber-300 via-pink-300 to-yellow-300 overflow-visible">
            <div className="absolute top-10 left-10 w-32 h-32 bg-white/20 rounded-full blur-2xl animate-pulse"></div>
            <div className="absolute bottom-10 right-10 w-40 h-40 bg-white/20 rounded-full blur-3xl animate-pulse"></div>
            
            {/* Profile Image - Large */}
            <div className="absolute -bottom-20 left-1/2 transform -translate-x-1/2">
              <div className="relative">
                {card.profileImage ? (
                  <img src={card.profileImage} alt={card.name} className="w-40 h-40 rounded-full border-4 border-white shadow-xl object-cover" />
                ) : (
                  <div className="w-40 h-40 rounded-full border-4 border-white shadow-xl bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center">
                    <span className="text-5xl font-bold text-white">{card.name?.charAt(0) || 'U'}</span>
                  </div>
                )}
                {isOwner && onUpdateProfileImage && (
                  <label htmlFor="profile-upload-modern" className="absolute bottom-2 right-2 bg-white rounded-full p-2 shadow-lg cursor-pointer hover:bg-amber-50">
                    <Pencil className="w-4 h-4 text-amber-600" />
                    <input id="profile-upload-modern" type="file" accept="image/*" onChange={onUpdateProfileImage} className="hidden" />
                  </label>
                )}
              </div>
            </div>
          </div>

          <div className="pt-24 pb-6 px-5">
            <div className="text-center mb-5">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-amber-600 to-pink-600 bg-clip-text text-transparent">{card.name}</h1>
                <Sparkles className="w-4 h-4 text-pink-500" />
              </div>
              {card.title && <p className="text-gray-600 text-sm flex items-center justify-center gap-1"><Briefcase className="w-3.5 h-3.5" />{card.title}</p>}
              {card.company && <p className="text-gray-500 text-xs flex items-center justify-center gap-1"><Building className="w-3 h-3" />{card.company}</p>}
            </div>

            <div className="space-y-2 mb-5">
              {card.phone && (
                <a href={`tel:${card.phone}`} onClick={() => trackLinkClick('phone', card.phone)} className="flex items-center gap-3 p-2.5 bg-gradient-to-r from-amber-50 to-pink-50 rounded-xl hover:from-amber-100 hover:to-pink-100 transition-colors border border-amber-200">
                  <Phone className="w-4 h-4 text-amber-600" /><span className="text-gray-700 text-sm">{card.phone}</span>
                </a>
              )}
              {card.email && (
                <a href={`mailto:${card.email}`} onClick={() => trackLinkClick('email', card.email)} className="flex items-center gap-3 p-2.5 bg-gradient-to-r from-amber-50 to-pink-50 rounded-xl hover:from-amber-100 hover:to-pink-100 transition-colors border border-amber-200">
                  <Mail className="w-4 h-4 text-amber-600" /><span className="text-gray-700 text-sm break-all">{card.email}</span>
                </a>
              )}
              {(card.website || (card.websiteName && card.websiteTLD)) && (
                <a href={card.website || `https://${card.websiteName}${card.websiteTLD}`} target="_blank" rel="noopener noreferrer" onClick={() => trackLinkClick('website', card.website)} className="flex items-center gap-3 p-2.5 bg-gradient-to-r from-amber-50 to-pink-50 rounded-xl hover:from-amber-100 hover:to-pink-100 transition-colors border border-amber-200">
                  <Globe className="w-4 h-4 text-amber-600" /><span className="text-gray-700 text-sm break-all">{(card.website || `${card.websiteName}${card.websiteTLD}`).replace(/^https?:\/\//, '')}</span>
                </a>
              )}
              {(card.location || card.locationAddress) && (
                <a href={
                  card.location && (card.location.startsWith('http://') || card.location.startsWith('https://'))
                    ? card.location 
                    : `https://maps.google.com/?q=${encodeURIComponent(card.locationAddress || card.location || '')}`
                } target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2.5 bg-gradient-to-r from-amber-50 to-pink-50 rounded-xl hover:from-amber-100 hover:to-pink-100 transition-colors border border-amber-200">
                  <MapPin className="w-4 h-4 text-amber-600" /><span className="text-gray-700 text-sm">{card.locationAddress || card.location || 'View Location'}</span>
                </a>
              )}
            </div>

            {socialLinks.length > 0 && (
              <div className="flex justify-center flex-wrap gap-3 mb-5">
                {socialLinks.map((social, index) => {
                  const BrandIcon = BrandIcons[social.type];
                  return (
                    <a key={index} href={constructSocialUrl(social.type, social.value)} target="_blank" rel="noopener noreferrer" onClick={() => trackLinkClick(social.type, social.value)} className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-50 to-pink-50 hover:from-amber-100 hover:to-pink-100 flex items-center justify-center transition-all hover:scale-110 border border-amber-200" title={social.label}>
                      {BrandIcon && <BrandIcon size={20} />}
                    </a>
                  );
                })}
              </div>
            )}

            {extraLinks.length > 0 && (
              <div className="space-y-2 mb-5">
                {extraLinks.map((link, index) => (
                  <a key={index} href={link.url} target="_blank" rel="noopener noreferrer" onClick={() => trackLinkClick('extraLink', link.url)} className="flex items-center gap-3 p-2.5 bg-gradient-to-r from-amber-100 to-pink-100 rounded-xl hover:from-amber-200 hover:to-pink-200 transition-colors border border-amber-300">
                    <ExternalLink className="w-4 h-4 text-amber-700" /><span className="text-amber-800 text-sm font-medium">{link.title}</span>
                  </a>
                ))}
              </div>
            )}

            {card.richBio && (
              <div className="bg-gradient-to-r from-amber-50 to-pink-50 rounded-xl p-4 mb-5 border border-amber-200">
                <div className={`overflow-hidden transition-all duration-300 text-sm text-gray-600 ${!isBioExpanded ? 'max-h-12' : 'max-h-[2000px]'}`}>
                  <BioRichTextDisplay content={card.richBio} />
                </div>
                <button onClick={() => setIsBioExpanded(!isBioExpanded)} className="flex items-center gap-1 mt-2 text-xs font-medium text-amber-600 hover:text-amber-700">
                  {isBioExpanded ? <><ChevronUp className="w-3.5 h-3.5" />Show less</> : <><ChevronDown className="w-3.5 h-3.5" />Read more</>}
                </button>
              </div>
            )}

            {hasGallery && (
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-3"><Images className="w-4 h-4 text-amber-600" /><h3 className="text-sm font-semibold text-gray-700">Gallery</h3></div>
                <GalleryGrid 
                  images={card.galleryImages} 
                  onDeleteImage={card.onDeleteImage} 
                  isOwner={isOwner}
                  accentColor="#d97706"
                />
              </div>
            )}

            {hasVideos && (
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-3"><Play className="w-4 h-4 text-amber-600" /><h3 className="text-sm font-semibold text-gray-700">Videos</h3></div>
                <div className="space-y-2">
                  {card.youtubeVideos.slice(0, 3).map((videoId, index) => {
                    const cleanVideoId = extractYouTubeVideoId(videoId);
                    return (
                      <div key={index} className="relative aspect-video rounded-lg overflow-hidden group">
                        <iframe src={`https://www.youtube.com/embed/${cleanVideoId}?rel=0`} title={`Video ${index + 1}`} className="w-full h-full" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                        {isOwner && card.onDeleteVideo && <button onClick={() => card.onDeleteVideo(index)} className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"><Trash2 className="w-3 h-3" /></button>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BioTemplateModern;
